/* A merchant's sign-in: the username and password Damga issued, then an emailed
 * code on a device that has not passed one.
 *
 * Ruled 2026-09-18 by the founder, for the merchant surface only (DECISIONS). The
 * server is backend/api/app/routers/merchant_auth.py; this is its client, used by
 * the dashboard and by the till's kiosk entry, which are one product.
 *
 * ONE REFUSAL, AND THIS FILE KEEPS IT ONE. The server answers a wrong password, an
 * unknown username, a paused sign-in and an account with no password yet with the
 * same bytes, so that nobody can learn which usernames exist. A client that
 * branched on anything in that body could undo it on screen, so every one of them
 * becomes `{ kind: "refused" }` here and nothing else is read.
 *
 * NO GOOGLE, NO MICROSOFT, NO EMAIL CODE TO SIGN IN. Those are the customer's
 * door and come off the merchant side entirely. The emailed code here is a second
 * factor after the password, never a way in by itself.
 */
import { Offline, type Session } from "./api";

export type SignInOutcome =
  | { kind: "signed_in"; session: Session }
  | { kind: "code_sent"; challenge: string }
  | { kind: "refused" }
  | { kind: "too_many_codes" }
  | { kind: "mail_down" };

export type CodeOutcome = { kind: "signed_in"; session: Session } | { kind: "refused" };

export type SetupCheck = { kind: "ok"; username: string } | { kind: "dead" };

export type SetupOutcome =
  | { kind: "ok"; username: string }
  | { kind: "dead" }
  | { kind: "weak"; reason: string };

/* A device that has passed the code once. Kept apart from the session, for the
 * reason the till's device id is: a shift ends and somebody signs out; the tablet
 * is the same tablet, and should not ask the next person for a code it already
 * passed. Revoked on the server when the password changes. */
const MERCHANT_DEVICE_KEY = "damga.merchant-device.v1";

export function readMerchantDevice(): string | null {
  try {
    return localStorage.getItem(MERCHANT_DEVICE_KEY);
  } catch {
    return null; // private browsing: the code is asked for every time, which is safe
  }
}

export function writeMerchantDevice(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(MERCHANT_DEVICE_KEY);
    else localStorage.setItem(MERCHANT_DEVICE_KEY, token);
  } catch {
    /* see above */
  }
}

/** Where the API client refreshes a merchant session: the API, not GoTrue. */
export function merchantRefreshUrl(apiUrl: string): string {
  return `${apiUrl}/merchant-auth/token`;
}

type TokenBody = { access_token?: string; refresh_token?: string; expires_in?: number };

function toSession(body: TokenBody): Session | null {
  if (!body.access_token || !body.refresh_token) return null;
  return {
    access_token: body.access_token,
    refresh_token: body.refresh_token,
    // A missing expires_in makes the refresh check NaN and the session dies an
    // hour later with nobody told why; the kit has been bitten by that twice.
    expires_at: Math.floor(Date.now() / 1000) + (body.expires_in ?? 3600),
  };
}

export function createMerchantAuth(apiUrl: string) {
  async function post(path: string, body: unknown): Promise<Response> {
    try {
      return await fetch(`${apiUrl}/merchant-auth${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch {
      throw new Offline();
    }
  }

  async function json(res: Response): Promise<Record<string, unknown>> {
    try {
      return (await res.json()) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  return {
    async signIn(username: string, password: string): Promise<SignInOutcome> {
      const res = await post("/sign-in", {
        username,
        password,
        device_token: readMerchantDevice() ?? undefined,
      });
      if (res.status === 429) return { kind: "too_many_codes" };
      if (res.status === 503) return { kind: "mail_down" };
      if (res.status >= 500) throw new Error(`server error ${res.status}`);
      if (!res.ok) return { kind: "refused" };
      const body = await json(res);
      if (body.status === "code_sent" && typeof body.challenge === "string") {
        return { kind: "code_sent", challenge: body.challenge };
      }
      const session = toSession(body as TokenBody);
      return session ? { kind: "signed_in", session } : { kind: "refused" };
    },

    async verifyCode(challenge: string, code: string): Promise<CodeOutcome> {
      const res = await post("/code", { challenge, code });
      if (res.status >= 500) throw new Error(`server error ${res.status}`);
      if (!res.ok) return { kind: "refused" };
      const body = await json(res);
      const session = toSession(body as TokenBody);
      if (!session) return { kind: "refused" };
      if (typeof body.device_token === "string") writeMerchantDevice(body.device_token);
      return { kind: "signed_in", session };
    },

    async checkSetup(token: string): Promise<SetupCheck> {
      const res = await post("/setup/check", { token });
      if (res.status >= 500) throw new Error(`server error ${res.status}`);
      if (!res.ok) return { kind: "dead" };
      const body = await json(res);
      return typeof body.username === "string" ? { kind: "ok", username: body.username } : { kind: "dead" };
    },

    async setPassword(token: string, password: string): Promise<SetupOutcome> {
      const res = await post("/setup", { token, password });
      if (res.status >= 500) throw new Error(`server error ${res.status}`);
      const body = await json(res);
      if (res.status === 422) return { kind: "weak", reason: String(body.reason ?? "") };
      if (!res.ok || typeof body.username !== "string") return { kind: "dead" };
      return { kind: "ok", username: body.username };
    },

    async signOut(refreshToken: string): Promise<void> {
      try {
        await post("/sign-out", { refresh_token: refreshToken });
      } catch {
        /* Signing out works offline: the session is dropped here either way,
           and the refresh token dies with its thirty days. */
      }
    },
  };
}

export type MerchantAuth = ReturnType<typeof createMerchantAuth>;
